import React from 'react';

interface State {
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

/**
 * B43 — Root-level error boundary. Catches render-tree failures in any child
 * component so that a single broken component doesn't blank out the whole
 * app. Falls back to a Nexus-themed panel with a Recargar action and an
 * optional details disclosure.
 *
 * Note: error boundaries do NOT catch errors inside async handlers, event
 * handlers, or server-side rendering. Those are still surfaced via toasts
 * (sonner) at the call site.
 */
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[Nexus ErrorBoundary]', error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div
        data-testid="error-boundary-fallback"
        className="min-h-screen bg-[#050505] flex items-center justify-center px-6"
      >
        <div className="max-w-xl w-full bg-[#0E0F11] border border-red-500/20 rounded-xl p-8">
          <p className="text-xs font-mono uppercase tracking-widest text-red-400 mb-3">
            Error inesperado
          </p>
          <h1 className="text-2xl text-white font-medium mb-3">
            Nexus encontró un error y detuvo este componente.
          </h1>
          <p className="text-sm text-[#A1A1AA] mb-6 leading-relaxed">
            Tus datos están a salvo. Recarga para volver al estado anterior. Si
            el error persiste, mira la consola del navegador y revisa{' '}
            <code className="text-white">/observability</code> para ver el
            evento correspondiente.
          </p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={this.handleReload}
              data-testid="error-boundary-reload"
              className="bg-white text-black font-medium rounded-md px-4 py-2 text-sm hover:bg-neutral-200 transition-colors"
            >
              Recargar
            </button>
            <a
              href="/observability"
              className="text-sm text-[#A1A1AA] hover:text-white underline"
            >
              Ir a Observabilidad
            </a>
          </div>
          {this.state.errorInfo && (
            <details className="mt-6 text-xs text-[#52525B] font-mono">
              <summary className="cursor-pointer hover:text-white">
                Detalle técnico
              </summary>
              <pre className="mt-2 whitespace-pre-wrap break-all">
                {this.state.error?.message}
                {'\n\n'}
                {this.state.errorInfo.componentStack}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
